import { Warehouse, Truck, Boxes } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtDate } from "@/lib/format";
import { TONE_HEX, alertCount, projectTone } from "./mapUtils";

const halo = "ops-label";

/**
 * Project site marker: progress ring (modules on site / planned), module count, alert dot and a label.
 * Colour = project status tone (planning → info, execution → warning, operating → success).
 */
export const ProjectMarker = ({ loc, selected, showLabel = true, onClick, size = 36 }) => {
  const p = loc.project || {};
  const tone = projectTone(p.status);
  const color = TONE_HEX[tone];
  const r = (size - 4) / 2;
  const circ = 2 * Math.PI * r;
  const progress = Math.max(0, Math.min(1, (p.progress_pct || 0) / 100));
  const alerts = alertCount(loc);
  const critical = (loc.alerts?.issues_critical || 0) + (loc.alerts?.conflicts || 0) > 0;
  const planned = !loc.modules_total; // nothing on site yet – show the planned count, dashed
  const busy = ["transport", "montasje", "demontering"].includes(p.status); // work happening on site this period
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${loc.name}${alerts ? `, ${alerts} forhold krever handling` : ""}`}
      aria-pressed={!!selected}
      className={cn("ops-marker group relative block cursor-pointer outline-none", selected && "z-10")}
      data-testid={`map-marker-project-${p.id || loc.id}`}
    >
      {/* soft site halo – tinted by status, stronger when work is happening */}
      <span className={cn("absolute -inset-2 rounded-full transition-transform duration-300 ease-out group-hover:scale-110", selected && "scale-110")} style={{ backgroundColor: color, opacity: busy ? 0.16 : planned ? 0.06 : 0.1 }} aria-hidden />
      {busy && <span className="absolute -inset-2 animate-[ops-pulse_2.8s_ease-out_infinite] rounded-full" style={{ backgroundColor: color, opacity: 0.3 }} aria-hidden />}
      <span className={cn("relative block transition-transform duration-200 ease-out group-hover:scale-105", selected && "scale-110")} style={{ width: size, height: size }}>
        <span className={cn("absolute inset-0 rounded-full bg-card shadow-[0_1px_2px_rgba(16,24,40,0.12),0_6px_14px_-6px_rgba(16,24,40,0.3)] transition-shadow duration-200", selected && "shadow-[0_0_0_5px_hsl(var(--primary)/0.18),0_4px_14px_-4px_rgba(16,24,40,0.35)]")} />
        <svg viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 h-full w-full -rotate-90" aria-hidden>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeOpacity={planned ? 0.55 : 0.22} strokeWidth={2.5} strokeDasharray={planned ? "2.5 3" : undefined} />
          {progress > 0 && <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" strokeDasharray={`${circ * progress} ${circ}`} />}
        </svg>
        <span className={cn("absolute inset-0 flex items-center justify-center text-[12px] font-semibold leading-none tabular", planned ? "text-muted-foreground" : "text-foreground")}>
          {loc.modules_total ? loc.modules_total : p.planned_modules ? p.planned_modules : <Boxes className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />}
        </span>
        {alerts > 0 && <span className={cn("absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full ring-2 ring-card", critical ? "bg-rose-500" : "bg-amber-500")} aria-hidden />}
      </span>
      <span className={cn("pointer-events-none absolute left-1/2 top-full mt-1 max-w-[200px] -translate-x-1/2 truncate whitespace-nowrap text-[11.5px] font-medium leading-4 text-foreground transition-opacity duration-150", halo, showLabel || selected ? "opacity-100" : "opacity-0 group-hover:opacity-100")}>{loc.short_name}</span>
    </button>
  );
};

/** Depot / warehouse marker: graphite rounded square with a fill gauge (occupancy) along the bottom edge. */
export const DepotMarker = ({ loc, selected, showLabel = true, onClick }) => {
  const size = loc.is_main ? 36 : 32;
  const fill = loc.capacity_modules ? Math.min(1, (loc.modules_total || 0) / loc.capacity_modules) : null;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${loc.name}, ${loc.modules_total} moduler`}
      aria-pressed={!!selected}
      className={cn("ops-marker group relative block cursor-pointer outline-none", selected && "z-10")}
      data-testid={`map-marker-depot-${loc.id}`}
    >
      <span className={cn("absolute -inset-1.5 rounded-[14px] transition-transform duration-300 ease-out group-hover:scale-110", selected && "scale-110")} style={{ backgroundColor: "hsl(var(--navy) / 0.1)" }} aria-hidden />
      <span
        className={cn("relative flex items-center justify-center overflow-hidden rounded-[10px] bg-navy text-white shadow-[0_2px_10px_-2px_rgba(16,24,40,0.5)] ring-2 ring-card transition-transform duration-200 ease-out group-hover:scale-105", selected && "scale-110 shadow-[0_0_0_5px_hsl(var(--primary)/0.18),0_2px_10px_-2px_rgba(16,24,40,0.5)]")}
        style={{ width: size, height: size }}
      >
        <Warehouse className="relative z-10 h-4 w-4" strokeWidth={1.75} />
        {fill != null && <span className="absolute inset-x-0 bottom-0 bg-white/[0.18]" style={{ height: `${Math.round(fill * 100)}%` }} aria-hidden />}
      </span>
      <span className={cn("pointer-events-none absolute left-1/2 top-full mt-1.5 max-w-[160px] -translate-x-1/2 truncate whitespace-nowrap text-[11.5px] font-medium leading-4 text-foreground transition-opacity duration-150", halo, showLabel || selected ? "opacity-100" : "opacity-0 group-hover:opacity-100")}>
        {loc.short_name} <span className="font-normal text-muted-foreground tabular">· {loc.modules_total}</span>
      </span>
    </button>
  );
};

/** Transport chip at the midpoint of a leg: count + state. «På vei» pulses gently; planned legs show the date. */
export const TransportChip = ({ t, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={`Transport ${t.transport_number}, ${t.module_count} moduler`}
    className={cn(
      "ops-marker flex h-6 cursor-pointer items-center gap-1.5 rounded-full bg-card/95 px-2 text-[11px] font-medium leading-none text-foreground shadow-pop backdrop-blur transition-transform duration-200 hover:scale-105",
      selected && "ring-2 ring-primary/50"
    )}
    data-testid={`map-transport-${t.id}`}
  >
    <Truck className="h-3 w-3 text-muted-foreground" strokeWidth={1.75} />
    <span className="tabular">{t.module_count}</span>
    {t.legs?.length > 1 && <span className="text-muted-foreground tabular">· {t.legs.length} turer</span>}
    <span className={cn("h-1.5 w-1.5 rounded-full", t.in_transit ? "bg-primary animate-pulse" : "bg-slate-400")} aria-hidden />
    <span className={cn(t.in_transit ? "text-primary" : "text-muted-foreground")}>{t.in_transit ? "På vei" : fmtDate(t.date, "d. MMM")}</span>
  </button>
);

/** Fleet map: modules grouped per location. Ring shows status mix. */
export const ModuleGroupMarker = ({ group, selected, showLabel = true, onClick, statusTone }) => {
  const size = group.count >= 100 ? 46 : group.count >= 20 ? 40 : 34;
  const r = (size - 5) / 2;
  const circ = 2 * Math.PI * r;
  const entries = Object.entries(group.by_status || {}).filter(([, n]) => n > 0);
  let acc = 0;
  const segs = entries.map(([s, n]) => {
    const len = (n / group.count) * circ;
    const seg = { s, len, off: acc };
    acc += len;
    return seg;
  });
  const isTransit = group.kind === "transit";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${group.name}, ${group.count} moduler`}
      aria-pressed={!!selected}
      className={cn("ops-marker group relative block cursor-pointer outline-none", selected && "z-10")}
      data-testid={`map-group-${group.location_id}`}
    >
      <span className={cn("relative block transition-transform duration-200 ease-out group-hover:scale-105", selected && "scale-110")} style={{ width: size, height: size }}>
        <span className={cn("absolute inset-0 rounded-full bg-card shadow-[0_1px_2px_rgba(16,24,40,0.12),0_4px_12px_-4px_rgba(16,24,40,0.25)]", selected && "shadow-[0_0_0_5px_hsl(var(--primary)/0.18),0_4px_14px_-4px_rgba(16,24,40,0.35)]")} />
        <svg viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 h-full w-full -rotate-90" aria-hidden>
          {segs.map((g) => (
            <circle key={g.s} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={TONE_HEX[statusTone(g.s)] || TONE_HEX.neutral} strokeWidth={3} strokeDasharray={`${g.len} ${circ}`} strokeDashoffset={-g.off} />
          ))}
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-[12px] font-semibold leading-none tabular text-foreground">{isTransit ? <Truck className="h-3.5 w-3.5" strokeWidth={1.75} /> : group.count}</span>
      </span>
      <span className={cn("pointer-events-none absolute left-1/2 top-full mt-1 max-w-[160px] -translate-x-1/2 truncate whitespace-nowrap text-[11.5px] font-medium leading-4 text-foreground transition-opacity duration-150", halo, showLabel || selected ? "opacity-100" : "opacity-0 group-hover:opacity-100")}>
        {group.short_name}
        {isTransit && <span className="font-normal text-muted-foreground tabular"> · {group.count}</span>}
      </span>
    </button>
  );
};

/**
 * Geographic cluster (zoomed out): a soft halo, a thin composition ring (project sites in green, depots in graphite),
 * the number of places and the module total. Click zooms into the group.
 */
export const ClusterMarker = ({ cluster, onClick }) => {
  const items = cluster.items || [];
  const projects = items.filter((i) => i.kind === "project_site");
  const depots = items.filter((i) => i.kind === "depot");
  const modules = items.reduce((s, i) => s + (i.modules_total || 0), 0);
  const alerts = items.reduce((s, i) => s + alertCount(i), 0);
  const critical = items.some((i) => (i.alerts?.issues_critical || 0) + (i.alerts?.conflicts || 0) > 0);
  const size = items.length >= 8 ? 58 : items.length >= 4 ? 52 : 46;
  const r = (size - 4) / 2;
  const circ = 2 * Math.PI * r;
  const pShare = items.length ? projects.length / items.length : 1;
  const gap = 3;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${items.length} steder, ${modules} moduler – zoom inn`}
      className="ops-marker group relative block cursor-zoom-in outline-none"
      data-testid={`map-cluster-${items.length}`}
    >
      <span className="absolute -inset-3 rounded-full bg-primary/[0.07] transition-transform duration-300 ease-out group-hover:scale-110" aria-hidden />
      <span className="absolute -inset-1.5 rounded-full bg-primary/[0.08] transition-transform duration-300 ease-out group-hover:scale-105" aria-hidden />
      <span className="relative block transition-transform duration-200 ease-out group-hover:scale-[1.04]" style={{ width: size, height: size }}>
        <span className="absolute inset-0 rounded-full bg-card shadow-[0_1px_2px_rgba(16,24,40,0.1),0_10px_24px_-10px_rgba(16,24,40,0.35)]" />
        <svg viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 h-full w-full -rotate-90" aria-hidden>
          {projects.length > 0 && <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={TONE_HEX.success} strokeOpacity={0.85} strokeWidth={2.5} strokeLinecap="round" strokeDasharray={`${Math.max(0, circ * pShare - (depots.length ? gap : 0))} ${circ}`} />}
          {depots.length > 0 && <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#1F2446" strokeOpacity={0.85} strokeWidth={2.5} strokeLinecap="round" strokeDasharray={`${Math.max(0, circ * (1 - pShare) - (projects.length ? gap : 0))} ${circ}`} strokeDashoffset={-(circ * pShare)} />}
        </svg>
        <span className="absolute inset-0 flex flex-col items-center justify-center leading-none">
          <span className="text-[15px] font-semibold tabular tracking-tight">{items.length}</span>
          <span className="mt-0.5 text-[9.5px] text-muted-foreground">{depots.length && !projects.length ? "depot" : "steder"}</span>
        </span>
        {alerts > 0 && <span className={cn("absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full ring-2 ring-card", critical ? "bg-rose-500" : "bg-amber-500")} aria-hidden />}
      </span>
      <span className={cn("pointer-events-none absolute left-1/2 top-full mt-1.5 -translate-x-1/2 whitespace-nowrap text-[11px] font-medium leading-4 text-foreground/80 tabular", halo)}>
        {modules} moduler{depots.length && projects.length ? ` · ${depots.length} depot` : ""}
      </span>
    </button>
  );
};
